import type {
  SurveyAnswerBody,
  SurveyQuestion,
  SurveyReward,
  SurveySession,
  SurveyStep,
  SurveyTriggerCode,
} from '../api/surveyApi.type';
import type { SurveyTriggerSite } from './surveyBus';

export const SETTLE_MS = 1500;
export const WAIT_FREE_MAX_MS = 60000;
export const WAIT_FREE_STEP_MS = 500;
export const CODE_THROTTLE_MS = 60000;
export const LOGIN_REPLAY_MS = 15000;
export const DEFAULT_TEXT_MAX_LENGTH = 255;

export type SurveyInlineError = 'answerRequired' | 'required' | 'tooMany' | 'otherRequired' | 'tooLong' | 'network';

export type SurveyView =
  | { kind: 'loading' }
  | { kind: 'intro'; session: SurveySession }
  | { kind: 'question'; step: SurveyStep; busy: boolean; error: SurveyInlineError | null; lastBody: SurveyAnswerBody | null }
  | { kind: 'final'; step: SurveyStep }
  | { kind: 'completeError'; token: string }
  | { kind: 'error'; reason: 'unavailable' | 'completed' };

export const SITE_CODES: Record<SurveyTriggerSite, SurveyTriggerCode[]> = {
  live: [ 'live', 'bet_settled' ],
  home: [ 'home', 'bet_settled' ],
  deposit_success: [ 'deposit_success' ],
  bet_settled: [ 'bet_settled' ],
};

export const pickCodes = (site: SurveyTriggerSite, lastChecked: Map<SurveyTriggerCode, number>, now: number): SurveyTriggerCode[] =>
  SITE_CODES[site].filter((code) => now - (lastChecked.get(code) ?? 0) >= CODE_THROTTLE_MS);

export const positiveOrNull = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
};

export const formatSum = (value: unknown): string => {
  const n = positiveOrNull(value);
  return n === null ? '' : String(n);
};

export type SurveyRewardLine = 'granted' | 'test' | 'unavailable' | null;

const UNAVAILABLE_REWARD_STATUSES = [ 'promo_used', 'bonus_disabled', 'already_claimed', 'no_currency_condition' ];

export const rewardLine = (reward: SurveyReward | null | undefined, teaserSum: number | null): SurveyRewardLine => {
  if (!reward) {
    return null;
  }
  if (reward.status === 'granted') {
    return positiveOrNull(reward.sum) === null ? null : 'granted';
  }
  if (reward.status === 'test') {
    return 'test';
  }
  if (teaserSum !== null && isRewardRefused(reward)) {
    return 'unavailable';
  }
  return null;
};

export const isRewardRefused = (reward: SurveyReward | null | undefined): boolean =>
  Boolean(reward && reward.status !== null && UNAVAILABLE_REWARD_STATUSES.includes(reward.status));

export const showFinalSubtitle = (subtitle: string | null | undefined, reward: SurveyReward | null | undefined): boolean =>
  Boolean(subtitle) && !isRewardRefused(reward);

export const RECOVER_CODES = [ 'SURVEY_STEP_MISMATCH', 'SURVEY_SESSION_CLOSED', 'SURVEY_NOT_FINISHED', 'SURVEY_ANSWER_INVALID' ];
export const UNAVAILABLE_CODES = [ 'SURVEY_UNAVAILABLE', 'resource_not_found', 'TRIGGER_INVALID' ];
export const COMPLETED_CODE = 'SURVEY_ALREADY_COMPLETED';

export const inlineErrorFor = (code: string, body: SurveyAnswerBody | null): SurveyInlineError => {
  if (code === 'SURVEY_ANSWER_REQUIRED') return body?.skip ? 'required' : 'answerRequired';
  if (code === 'SURVEY_TOO_MANY_OPTIONS') return 'tooMany';
  if (code === 'SURVEY_OTHER_TEXT_REQUIRED') return 'otherRequired';
  if (code === 'SURVEY_TEXT_TOO_LONG') return 'tooLong';
  return 'network';
};

export const INLINE_ERROR_MESSAGE_IDS: Record<SurveyInlineError, string> = {
  answerRequired: 'SURVEY_ERROR_ANSWER_REQUIRED',
  required: 'SURVEY_ERROR_REQUIRED',
  tooMany: 'SURVEY_ERROR_TOO_MANY',
  otherRequired: 'SURVEY_ERROR_OTHER_REQUIRED',
  tooLong: 'SURVEY_ERROR_TOO_LONG',
  network: 'SURVEY_ERROR_NETWORK',
};

const SURVEY_ERROR_FIELDS = [ 'session', 'survey', 'questionId', 'answerIds', 'skip', 'otherText', 'text', 'code' ];

export const surveyErrorCode = (error: unknown): string => {
  if (!error || typeof error !== 'object') return 'NETWORK';
  const { status, data } = error as { status?: unknown; data?: unknown };
  if (status === 401) return 'AUTH';
  if (status !== 'VALIDATION_ERROR') return 'NETWORK';
  if (typeof data === 'string') return data;
  if (data && typeof data === 'object') {
    for (const field of SURVEY_ERROR_FIELDS) {
      const value = (data as Record<string, unknown>)[field];
      if (Array.isArray(value) && value.length > 0) return String(value[0]);
    }
  }
  return 'VALIDATION';
};

const UPLOAD_RE = /^upload:(survey\/[0-9a-f]{32}\.(?:svg|png|gif))$/;
const FONT_RE = /^font:([a-z0-9_]{1,40})$/;

export type SurveyAsset = { kind: 'font'; alias: string } | { kind: 'url'; url: string } | null;

export const resolveSurveyAsset = (value: string | null | undefined, cdnBase: string): SurveyAsset => {
  if (!value) return null;
  const font = FONT_RE.exec(value);
  if (font) return { kind: 'font', alias: font[1] };
  const upload = UPLOAD_RE.exec(value);
  if (upload) return { kind: 'url', url: `${cdnBase.replace(/\/$/, '')}/${upload[1]}` };
  return null;
};

export const hasOtherOption = (question: SurveyQuestion): boolean => question.answers.some((answer) => answer.isOther);

export const isAutoAdvance = (question: SurveyQuestion): boolean =>
  question.type === 'single' && question.autoAdvance && !hasOtherOption(question);

export const selectionCap = (question: SurveyQuestion): number => {
  if (question.type === 'single') return 1;
  const max = positiveOrNull(question.maxSelections);
  return max === null ? question.answers.length : Math.min(max, question.answers.length);
};

export const toggleSelection = (question: SurveyQuestion, selected: number[], answerId: number): number[] => {
  if (question.type === 'single') {
    return [ answerId ];
  }
  if (selected.includes(answerId)) {
    return selected.filter((id) => id !== answerId);
  }
  if (selected.length >= selectionCap(question)) {
    return selected;
  }
  return [ ...selected, answerId ];
};

export const isOtherSelected = (question: SurveyQuestion, selected: number[]): boolean =>
  question.answers.some((answer) => answer.isOther && selected.includes(answer.id));

export const textMaxLength = (question: SurveyQuestion): number => positiveOrNull(question.textMaxLength) ?? DEFAULT_TEXT_MAX_LENGTH;

export const otherMaxLength = (question: SurveyQuestion): number => {
  const other = question.answers.find((answer) => answer.isOther);
  return positiveOrNull(other?.otherMaxLength) ?? DEFAULT_TEXT_MAX_LENGTH;
};

export const canSubmitAnswer = (question: SurveyQuestion, selected: number[], otherText: string, text: string): boolean => {
  if (question.type === 'text') {
    const trimmed = text.trim();
    return trimmed.length >= 1 && trimmed.length <= textMaxLength(question);
  }
  if (selected.length < 1 || selected.length > selectionCap(question)) {
    return false;
  }
  if (isOtherSelected(question, selected)) {
    const trimmed = otherText.trim();
    return trimmed.length >= 1 && trimmed.length <= otherMaxLength(question);
  }
  return true;
};

export const buildAnswerBody = (question: SurveyQuestion, selected: number[], otherText: string, text: string): SurveyAnswerBody => ({
  questionId: question.id,
  answerIds: question.type === 'text' ? [] : selected,
  otherText: question.type !== 'text' && isOtherSelected(question, selected) ? otherText.trim() : null,
  text: question.type === 'text' ? text.trim() : null,
  skip: false,
});

export const buildSkipBody = (question: SurveyQuestion): SurveyAnswerBody => ({
  questionId: question.id,
  answerIds: [],
  otherText: null,
  text: null,
  skip: true,
});

export const progressValue = (current: number, total: number): number => {
  if (!total || total <= 0) return 0;
  const value = ((current - 1) / total) * 100;
  return Math.max(0, Math.min(100, value));
};
